import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { CircleDollarSign, Gift } from 'lucide-react';
import { Badge } from './Badge';
import clsx from 'clsx';
import styles from './CollabTypeBadge.module.scss';

/**
 * Evaluates whether an offer or application represents a Paid Collab or Gifting.
 * Handles top-level offer objects, nested offer_details, and application models.
 */
export const isPaidOffer = (offer) => {
  if (!offer) return false;
  const target = offer.offer_details || offer.offer || offer;

  // Explicit compensation modes
  const mode = String(target?.compensation_mode || offer?.compensation_mode || '').toLowerCase();
  if (['paid', 'hybrid'].includes(mode)) return true;

  const type = String(target?.compensation_type || offer?.compensationType || offer?.compensation_type || '').toLowerCase();
  if (['paid', 'hybrid'].includes(type)) return true;

  // Sponsor flags
  if (Boolean(target?.sponsor_this_offer) || Boolean(offer?.sponsor_this_offer)) return true;

  // Explicit numeric monetary amounts
  if (Number(target?.cash_compensation || 0) > 0) return true;
  if (Number(offer?.cash_compensation || 0) > 0) return true;
  if (Number(target?.sponsor_amount || 0) > 0) return true;
  if (Number(offer?.sponsor_amount || 0) > 0) return true;
  if (Number(offer?.cashEarnings || 0) > 0) return true;
  if (Number(offer?.earnings || 0) > 0) return true;

  return false;
};

/**
 * Standard Design System Badge for Collaboration Type (Paid Collab vs. Gifting).
 */
export const CollabTypeBadge = ({
  offer,
  isPaid,
  type,
  size = 'sm',
  showIcon = true,
  className,
  ...props
}) => {
  const { t } = useTranslation('common');

  const paid = typeof isPaid === 'boolean'
    ? isPaid
    : type
      ? type === 'paid'
      : isPaidOffer(offer);

  const variant = paid ? 'paid' : 'gifting';
  const Icon = showIcon ? (paid ? CircleDollarSign : Gift) : null;
  const label = paid
    ? t('collab_tags.paid', 'Paid Collab')
    : t('collab_tags.gifting', 'Gifting');

  return (
    <Badge
      variant={variant}
      size={size}
      icon={Icon}
      className={clsx(
        styles.collabTypeBadge,
        styles[`collabTypeBadge--${variant}`],
        className
      )}
      data-collab-type={variant}
      {...props}
    >
      {label}
    </Badge>
  );
};

CollabTypeBadge.propTypes = {
  offer: PropTypes.object,
  isPaid: PropTypes.bool,
  type: PropTypes.oneOf(['paid', 'gifting']),
  size: PropTypes.oneOf(['sm', 'md']),
  showIcon: PropTypes.bool,
  className: PropTypes.string,
};

export default CollabTypeBadge;
